import api from './api';

export const authService = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    console.log('[AuthService] Login response received');
    console.log('[AuthService] Has token:', !!response.data.token);
    console.log('[AuthService] Has refreshToken:', !!response.data.refreshToken);
    
    if (response.data.token) {
      // Полная очистка старых данных перед входом
      const keysToPreserve = ['pendingReferralCode', 'pendingFcmToken', 'pendingNavigation'];
      const preservedData = {};
      keysToPreserve.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) preservedData[key] = value;
      });
      
      localStorage.clear();
      
      // Восстанавливаем временные данные
      Object.keys(preservedData).forEach(key => {
        localStorage.setItem(key, preservedData[key]);
      });
      
      // Сохраняем данные нового пользователя
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Сохраняем refresh token
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
        console.log('[AuthService] Refresh token saved to localStorage');
      } else {
        console.warn('[AuthService] WARNING: No refresh token in response!');
      }
      
      // Вызываем кастомное событие для обновления UI
      window.dispatchEvent(new Event('authChange'));
    }
    return response.data;
  },

  register: async (userData, referralCode = null) => {
    const payload = referralCode ? { ...userData, referralCode } : userData;
    const response = await api.post('/auth/register', payload);
    if (response.data.token) {
      // Полная очистка старых данных перед регистрацией
      const keysToPreserve = ['pendingReferralCode', 'pendingFcmToken'];
      const preservedData = {};
      keysToPreserve.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) preservedData[key] = value;
      });
      
      localStorage.clear();
      
      // Восстанавливаем временные данные
      Object.keys(preservedData).forEach(key => {
        localStorage.setItem(key, preservedData[key]);
      });
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Сохраняем refresh token
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
      
      // Вызываем кастомное событие для обновления UI
      window.dispatchEvent(new Event('authChange'));
    }
    return response.data;
  },

  registerWithReferral: async (referralCode, userData) => {
    try {
      // First validate the referral code
      await api.post('/referrals/validate', { referralCode });
      
      // Then proceed with registration
      const response = await api.post('/auth/register', {
        ...userData,
        referralCode
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.error || 'Неверный реферальный код');
      }
      throw error;
    }
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  applyReferralCode: async (referralCode) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // First validate the referral code
      await api.post('/referrals/validate', { referralCode });
      
      // Then apply it to the user's account
      const response = await api.post('/auth/apply-referral', { 
        userId: user.id,
        referralCode 
      });

      // Update the stored user data
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.error || 'Неверный или истёкший реферальный код');
      }
      if (error.response?.status === 409) {
        throw new Error(error.response?.data?.error || 'У вас уже есть реферер');
      }
      throw error;
    }
  },

  verifyPhone: async (phone, code) => {
    try {
      const response = await api.post('/auth/verify-phone', { phone, code });
      // If server returned token + user (auto-login after verification)
      if (response.data.token && response.data.user) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Сохраняем refresh token
        if (response.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken);
        }
        
        window.dispatchEvent(new Event('authChange'));
      } else if (response.data.verified) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({
          ...user,
          phoneVerified: true
        }));
      }
      return response.data;
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.error || 'Неверный код подтверждения');
      }
      throw error;
    }
  },

  resendVerificationCode: async (phone) => {
    try {
      const response = await api.post('/auth/resend-verification', { phone });
      return response.data;
    } catch (error) {
      if (error.response?.status === 429) {
        // Return the exact error message from server (already in Russian)
        throw new Error(error.response?.data?.error || 'Слишком много попыток. Пожалуйста, подождите.');
      }
      throw error;
    }
  },

  logout: () => {
    // Сохраняем только критичные данные для мобильного приложения
    const keysToPreserve = ['pendingFcmToken'];
    const preservedData = {};
    keysToPreserve.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) preservedData[key] = value;
    });
    
    // Полная очистка (включая savedCredentials)
    localStorage.clear();
    
    // Восстанавливаем только FCM токен для мобильного приложения
    Object.keys(preservedData).forEach(key => {
      localStorage.setItem(key, preservedData[key]);
    });
    
    // Вызываем кастомное событие для обновления UI
    window.dispatchEvent(new Event('authChange'));
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  isPhoneVerified: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user).phoneVerified : false;
  },

  updateUserData: (userData) => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    localStorage.setItem('user', JSON.stringify({
      ...currentUser,
      ...userData
    }));
  }
};